<template>
    <div class="connection-apps-card">
        <!-- Header Section -->
        <div class="connection-apps-header">
            <div class="header-left">
                <h4 class="header-title">
                    <i class="fas fa-key me-2 primary-text"></i>Custom Acces Token Integration
                </h4>
                <p class="header-subtitle">Manage access token customization for integration of other systems into membership</p>
            </div>
            <div class="header-actions">
                <button @click="showOnboarding" class="btn-secondary-action">
                    <i class="fas fa-question-circle me-2"></i>
                    <span class="btn-text">About</span>
                </button>
                <button @click="goToCreate" class="btn-primary-action" title="Create new token">
                    <i class="fas fa-plus me-1"></i>
                    <span class="btn-text">New Token</span>
                </button>
            </div>
        </div>

        <div class="connection-apps-content">
            <div class="filter-action-wrapper mb-4 mt-3">
                <div class="filter-controls">
                    <!-- Search Control -->
                    <div class="search-box-custom">
                        <div class="search-type-tabs">
                            <button 
                                v-for="opt in searchOptions" 
                                :key="opt.value"
                                class="tab-btn" 
                                :class="{ active: searchSelected === opt.value }"
                                @click="searchSelected = opt.value"
                            >
                                {{ opt.text }}
                            </button>
                        </div>

                        <div class="search-input-wrapper" v-show="searchSelected === 'name'">
                            <input 
                                type="text" 
                                class="form-control-search" 
                                v-model="searchCriteriaName" 
                                placeholder="Search by token name..." 
                                @keyup.enter="doSearch()" 
                            />
                            <button v-if="!isSearchActive" class="btn-search" @click="doSearch()">
                                <i class="fas fa-search"></i>
                            </button>
                            <button v-else class="btn-search" @click="clearSearch()">
                                <i class="fas fa-times text-danger"></i>
                            </button>
                        </div>
                        
                        <!-- Search by Branch Dropdown -->
                        <div class="search-input-wrapper" v-show="searchSelected === 'branch_id'">
                            <div class="input-container-search">
                                <select class="form-select custom-select-transparent" v-model="searchCriteriaBranch" @change="doSearch()">
                                    <option value="">Select Branch</option>
                                    <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
                                </select>
                            </div>
                            <button v-if="isSearchActive" class="btn-search" @click="clearSearch()">
                                <i class="fas fa-times text-danger"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="action-section">
                    <div class="action-buttons-group">
                        <button class="btn btn-refresh" @click.prevent="resetState()" title="Refresh" :disabled="isLoading">
                            <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="isLoading" class="loading-state">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <p class="loading-text">Loading access tokens...</p>
            </div>

            <div v-else>
                <o-table style="text-align:left;" class="table-hover member-list-table">
                    <thead>
                        <tr>
                            <td class="fw-bolder text-muted" scope="col" v-for="(field, index) in fields" :key="index">
                                {{ field }}
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(token, index) in tokens" :key="token.id" class="member-row-clickable">
                            <td>{{ ((meta.page - 1) * meta.limit) + index + 1 }}</td>
                            <td>
                                <span class="token-name">{{ token.name.length > 20 ? token.name.substring(0, 20) + '..' : token.name }}</span>
                            </td>
                            <td>{{ token.branch_name || '-' }}</td>
                            <td>
                                <template v-if="token.assigned_to">
                                    <span class="user-name">{{ token.assigned_to.name }}</span>
                                    <span class="user-email text-muted" style="font-size: 12px">{{ token.assigned_to.email }}</span>
                                </template>
                                <span v-else>-</span>
                            </td>
                            <td>{{ token.created_by_name || '-' }}</td>
                            <td>
                                <span class="date-text" v-if="token.expires_at">{{ formatDate(token.expires_at) }}</span>
                                <span class="date-text" v-else>Never</span>
                            </td>
                            <td class="ml-3">
                                <div class="d-flex gap-2" @click.stop>
                                    <button class="btn btn-action btn-info-action" @click="showDetail(token)" title="View Detail">
                                        <i class="fas fa-eye me-1"></i> Info
                                    </button>
                                    <button class="btn btn-action btn-delete" @click="confirmRevoke(token)" title="Revoke Token">
                                        <i class="fas fa-trash-alt me-1"></i> Revoke
                                    </button>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="tokens.length === 0">
                            <td :colspan="fields.length" class="text-center">
                                <div class="p-3 text-muted">
                                    No custom API tokens found.
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </o-table>

                <div class="container mt-1" v-if="tokens.length > 0 && meta.total > 1">
                    <o-pagination :page="meta.page" :total="meta.total" @callback="changePage"/>
                </div>
            </div>
        </div>
            
        <!-- Detail Modal -->
        <o-modal v-if="showDetailModal" @close="showDetailModal = false" modal-class="modal-detail">
            <template v-slot:header>
                <div class="modal-header-custom" style="display:flex; justify-content:space-between; width:100%;">
                    <h4 style="margin:0; font-size:1.25rem; font-weight:600; color:#111827;">Token Detail</h4>
                </div>
            </template>
            <template v-slot:body>
                <div class="detail-body" v-if="selectedToken">
                    <div class="detail-row">
                        <span class="detail-label">Token Name</span>
                        <span class="detail-value">{{ selectedToken.name }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Branch</span>
                        <span class="detail-value">{{ selectedToken.branch_name || '-' }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Assigned To User</span>
                        <span class="detail-value" v-if="selectedToken.assigned_to">
                            {{ selectedToken.assigned_to.name }} ({{ selectedToken.assigned_to.email }})
                        </span>
                        <span class="detail-value" v-else>-</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Created By</span>
                        <span class="detail-value">{{ selectedToken.created_by_name || '-' }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Created At</span>
                        <span class="detail-value">{{ formatDate(selectedToken.created_at) }}</span>
                    </div>
                    <div class="detail-row">
                        <span class="detail-label">Expires</span>
                        <span class="detail-value">{{ selectedToken.expires_at ? formatDate(selectedToken.expires_at) : 'Never' }}</span>
                    </div>
                    <div class="detail-row detail-abilities">
                        <span class="detail-label">Permissions</span>
                        <div class="detail-value">
                            <span v-for="(ability, i) in groupAbilities(selectedToken.abilities)" :key="i" class="ability-tag">
                                {{ ability }}
                            </span>
                            <span v-if="!selectedToken.abilities || selectedToken.abilities.length === 0">-</span>
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:footer>
                <button class="btn btn-outline-dynamic" @click="showDetailModal = false">Close</button>
            </template>
        </o-modal>

        <!-- Revoke Confirmation Modal -->
        <o-modal v-if="showRevokeModal" @close="showRevokeModal = false">
            <template v-slot:header>
                <div style="display:flex; align-items:center; gap: 8px;">
                    <i class="fas fa-unlink text-danger" style="font-size: 1.25rem;"></i>
                    <h4 style="margin:0; font-size:1.25rem; font-weight:600; color:#111827;">Revoke Token</h4>
                </div>
            </template>
            <template v-slot:body>
                <div class="alert alert-danger" style="margin-bottom: 1rem; display: flex; gap: 0.5rem; align-items: flex-start;">
                    <i class="fas fa-exclamation-triangle" style="margin-top: 3px;"></i>
                    <div>
                        <strong>Warning: This action cannot be undone.</strong>
                    </div>
                </div>
                <p style="color:#6b7280; font-size:0.9375rem;">Are you sure you want to revoke <strong>"{{ tokenToRevoke?.name }}"</strong>?</p>
                <p style="color:#6b7280; font-size:0.9375rem; margin-bottom:1rem;">Revoking this token will permanently disable the following:</p>
                <ul class="delete-list" style="list-style: none; padding: 0; margin-top: 1rem; display: flex; flex-direction: column; gap: 10px; color:#374151; font-size:14px;">
                    <li style="display: flex; gap: 10px; align-items: flex-start;">
                        <i class="fas fa-plug text-danger" style="margin-top: 3px;"></i> 
                        <div><strong>Integration Access:</strong> Any external system using this token will lose access to Membership APIs immediately.</div>
                    </li>
                    <li style="display: flex; gap: 10px; align-items: flex-start;">
                        <i class="fas fa-ban text-danger" style="margin-top: 3px;"></i> 
                        <div><strong>Failed Requests:</strong> In-flight requests using this token will begin returning 401 Unauthorized errors.</div>
                    </li>
                </ul>
            </template>
            <template v-slot:footer>
                <button @click="showRevokeModal = false" class="btn btn-outline-dynamic">
                    Cancel
                </button>
                <button @click="executeRevoke" class="btn btn-action btn-delete" :disabled="isRevoking" style="padding: 10px 16px;">
                    <span v-if="isRevoking"><i class="fas fa-spinner fa-spin me-1"></i> Revoking...</span>
                    <span v-else>Yes, Revoke</span>
                </button>
            </template>
        </o-modal>

        <!-- Onboarding Modal -->
        <o-modal v-if="isOnboardingVisible" @close="closeOnboarding" modal-class="show d-block">
            <template v-slot:header>
                <h5 class="modal-title">
                    <i class="fas fa-info-circle primary-text me-2"></i>About Custom API Tokens
                </h5>
            </template>
            <template v-slot:body>
                <div class="onboarding-content" id="onboarding-scroll-content">
                    <div class="feature-section">
                        <div class="feature-icon">
                            <i class="fas fa-key"></i>
                        </div>
                        <h6>Custom API Tokens</h6>
                        <p>Manage access token customization for integration of other systems into membership.</p>
                    </div>

                    <div class="alert alert-warning mb-4" style="display: flex; gap: 0.5rem; align-items: flex-start; margin-bottom: 0; background-color: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 1rem 1.25rem;">
                        <i class="fas fa-lightbulb text-warning" style="margin-top: 3px; color: #d97706;"></i>
                        <div style="color: #92400e;">
                            <strong>Recommendation:</strong> Use a dedicated "API User" (not a personal account) for production integration, so the token won't be revoked when the assigned user resigns.
                        </div>
                    </div>
                    
                    <div class="features-list">
                        <h6>Key Features:</h6>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span><b>Granular Permission</b> — Choose accessible modules & actions per token</span>
                        </div>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span><b>Branch-Level Scope</b> — Tokens are valid at the Branch level, covering all its merchants</span>
                        </div>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span><b>Assign to Existing User</b> — Tokens are generated on behalf of registered users</span>
                        </div>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span><b>Token Lifecycle</b> — Token will become invalid if the assigned user is deleted or disabled</span>
                        </div>
                        <div class="feature-item">
                            <i class="fas fa-check-circle primary-text"></i>
                            <span><b>Tenant Isolation</b> — Complete data isolation between tenants</span>
                        </div>
                    </div>
                </div>
            </template>
        </o-modal>
    </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { OTable, OPagination, OModal } from '@/components/OmnihControl'
import { useToast } from '@/composables/useToast'

export default {
    name: 'CustomTokenList',
    components: {
        OTable,
        OPagination,
        OModal
    },
    setup() {
        const router = useRouter()
        const store = useStore()
        const { showToast } = useToast()

        // Search logic
        const isSearchActive = ref(false)
        const searchSelected = ref('name')
        const searchCriteriaName = ref('')
        const searchCriteriaBranch = ref('')

        const searchOptions = reactive([
            { value: 'name', text: 'Token Name' },
            { value: 'branch_id', text: 'Branch' }
        ])

        const isOnboardingVisible = ref(false)
        const showDetailModal = ref(false)
        const showRevokeModal = ref(false)
        const selectedToken = ref(null)
        const tokenToRevoke = ref(null)
        const isRevoking = ref(false)

        const fields = ref([
            'No',
            'Token Name',
            'Branch',
            'Assigned To User',
            'Created By',
            'Expires',
            'Actions'
        ])

        const tokens = computed(() => store.getters['CustomToken/tokens'] || [])
        const meta = computed(() => store.state.CustomToken?.meta || { page: 1, limit: 15, total: 1 })
        const isLoading = computed(() => store.getters['CustomToken/status'] === 'loading')
        const branches = ref([])

        function loadTokens() {
            store.dispatch('CustomToken/read')
        }

        function doSearch() {
            const selected = searchSelected.value
            let criteria = ''
            
            if (selected === 'name') {
                criteria = searchCriteriaName.value
            } else if (selected === 'branch_id') {
                criteria = searchCriteriaBranch.value
            }

            if (!criteria && selected !== 'branch_id') return

            isSearchActive.value = true
            let search = {}
            if (criteria) {
                search[selected] = criteria
            }

            store.commit('CustomToken/set_search', { search })
            store.commit('CustomToken/switch_page', { meta: { ...meta.value, page: 1 } })
            loadTokens()
        }

        function clearSearch() {
            isSearchActive.value = false
            searchCriteriaName.value = ''
            searchCriteriaBranch.value = ''
            store.commit('CustomToken/clear_search')
            store.commit('CustomToken/switch_page', { meta: { ...meta.value, page: 1 } })
            loadTokens()
        }

        function resetState() {
            isSearchActive.value = false
            searchCriteriaName.value = ''
            searchCriteriaBranch.value = ''
            store.commit('CustomToken/clear_search')
            store.commit('CustomToken/switch_page', { meta: { ...meta.value, page: 1 } })
            loadTokens()
        }

        function changePage(page) {
            store.commit('CustomToken/switch_page', {
                meta: { ...meta.value, page }
            })
            loadTokens()
        }

        function goToCreate() {
            router.push({ name: 'CustomTokenCreate' })
        }

        function showOnboarding() {
            isOnboardingVisible.value = true
            nextTick(() => {
                const scrollContainer = document.getElementById('onboarding-scroll-content')
                if (scrollContainer) {
                    scrollContainer.scrollTop = 0
                }
            })
        }

        function closeOnboarding() {
            isOnboardingVisible.value = false
        }

        function showDetail(token) {
            selectedToken.value = token
            showDetailModal.value = true
        }

        function confirmRevoke(token) {
            tokenToRevoke.value = token
            showRevokeModal.value = true
        }

        async function executeRevoke() {
            if (!tokenToRevoke.value) return
            isRevoking.value = true
            try {
                await store.dispatch('CustomToken/revoke', tokenToRevoke.value.id)
                showRevokeModal.value = false
                tokenToRevoke.value = null
                showToast({
                    type: 'success',
                    title: 'Token Revoked',
                    message: 'Custom token successfully revoked.'
                })
                loadTokens()
            } catch (err) {
                console.error('Error revoking token:', err)
                showToast({
                    type: 'error',
                    title: 'Revoke Failed',
                    message: err.response?.data?.message || 'Failed to revoke custom token.'
                })
            } finally {
                isRevoking.value = false
            }
        }

        function formatDate(dateStr) {
            if (!dateStr) return '-'
            const d = new Date(dateStr)
            return d.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            })
        }

        function groupAbilities(abilities) {
            if (!abilities || abilities.length === 0) return []
            const groups = {}
            abilities.forEach(ability => {
                const parts = ability.split(':')
                if (parts.length === 2) {
                    const module = parts[0]
                    const action = parts[1]
                    if (!groups[module]) groups[module] = []
                    groups[module].push(action)
                }
            })
            return Object.entries(groups).map(([module, actions]) => {
                if (actions.length > 0) {
                    return `${module} : ${actions.join(', ')}`
                }
                return module
            })
        }

        onMounted(() => {
            store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                store.dispatch('Branch/read_all', businessProfile.corporate_id).then(res => {
                    if (businessProfile.branch_id) {
                        branches.value = res.filter(branch => branch.id === businessProfile.branch_id)
                    } else {
                        branches.value = res
                    }
                })
            })
            
            store.commit('CustomToken/clear_search')
            loadTokens()
        })

        return {
            fields,
            tokens,
            meta,
            isLoading,
            branches,
            isOnboardingVisible,
            showDetailModal,
            showRevokeModal,
            selectedToken,
            tokenToRevoke,
            isRevoking,       
            searchOptions,
            searchSelected,
            searchCriteriaName,
            searchCriteriaBranch,
            isSearchActive,
            doSearch,
            clearSearch,
            resetState,
            changePage,
            goToCreate,
            showOnboarding,
            closeOnboarding,
            showDetail,
            confirmRevoke,
            executeRevoke,
            formatDate,
            groupAbilities
        }
    }
}
</script>

<style scoped>
/* Container & Card */
.connection-apps-card {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
}

/* Header Section */
.connection-apps-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
}

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
}

.loading-text {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 0.875rem;
}

.header-left {
    flex: 1;
}

.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}

.header-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
}

.primary-text {
    color: var(--primary-color, #F93E3E);
}

.btn-primary-action {
    background: var(--primary-color, #F93E3E);
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-primary-action:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
}

.btn-secondary-action {
    background: #f3f4f6;
    color: #4b5563;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-secondary-action:hover {
    background: #e5e7eb;
    color: #1f2937;
    transform: translateY(-1px);
}

/* Content & Filter Bar */
.connection-apps-content {
    padding: 0.5rem 1rem;
}

/* Filter and Action Bar Styling from Member.vue */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Custom Search Box */
.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: max-content;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
    flex-wrap: wrap;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color, #F93E3E);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 2px var(--secondary-color, #Fee9E9);
}

.search-input-wrapper input,
.search-input-wrapper select.custom-select-transparent {
    border: none;
    padding: 10px 16px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
    box-shadow: none;
}

.search-input-wrapper select.custom-select-transparent:focus {
    box-shadow: none;
    border: none;
}

.input-container-search {
    position: relative;
    width: 100%;
    flex: 1;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color, #F93E3E);
}

.search-input-wrapper .btn-search {
    position: relative;
    width: 44px;
    height: 42px;
    border-radius: 0 8px 8px 0;
    background: transparent;
}

.search-input-wrapper .btn-search:hover {
    background: #f1f5f9;
}

.search-input-wrapper .btn-search::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1.1px;
    height: 60%;
    background: linear-gradient(to bottom, rgba(203, 213, 225, 0), rgba(203, 213, 225, 1), rgba(203, 213, 225, 0));
    pointer-events: none;
}

/* Action Buttons */
.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 8px;
}

.btn-refresh:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

/* Table Styles from Member */
.member-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    width: 100%;
}

.member-list-table th,
.member-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.member-row-clickable {
    transition: background-color 0.2s;
}

.member-row-clickable:hover {
    background-color: #f8fafc !important;
}

.token-name {
    display: block;
    max-width: 350px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
}

.date-text {
    font-size: 13px;
    color: #4a5568;
    white-space: nowrap;
}

.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
    cursor: pointer;
}

.btn-info-action {
    background-color: #eff6ff;
    color: #1e40af;
}

.btn-info-action:hover {
    background-color: #dbeafe;
    color: #1e3a8a;
}

.btn-delete {
    background-color: #fff1f2;
    color: #9f1239;
}

.btn-delete:hover {
    background-color: #ffe4e6;
    color: #be123c;
}

.token-level-badge {
    display: inline-block;
    padding: 0.125rem 0.375rem;
    background: #eff6ff;
    color: #1d4ed8;
    border-radius: 4px;
    font-size: 0.6875rem;
    font-weight: 500;
    text-transform: uppercase;
}

.user-name {
    display: block;
    font-weight: 600;
    font-size: 14px;
    color: #111827;
}

.user-email {
    display: block;
    font-size: 0.75rem;
    color: #9ca3af;
}

/* Onboarding Modal Content */
.onboarding-content {
    padding: 1rem;
    max-height: 65vh;
    overflow-y: auto;
}

.feature-section {
    text-align: center;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.feature-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    background: var(--primary-gradient, linear-gradient(135deg, #F27178 0%, #F27178 100%));
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 2rem;
}

.feature-section h6 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
}

.feature-section p {
    color: #6b7280;
    margin: 0;
}

.features-list h6 {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 1rem;
}

.feature-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 0;
}

.feature-item i {
    margin-top: 2px;
    font-size: 1rem;
}

.feature-item span {
    color: #374151;
    font-size: 0.9375rem;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 1rem;
}

.modal-content {
    background: white;
    border-radius: 16px;
    padding: 2rem;
    max-width: 500px;
    width: 90%;
}

.modal-detail {
    max-width: 600px;
}

.modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
}

.modal-header-custom h4 {
    color: #111827;
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
}

.btn-close-modal {
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 1.125rem;
    cursor: pointer;
    padding: 0.25rem;
}

.btn-close-modal:hover {
    color: #374151;
}

.btn-outline-dynamic {
    padding: 8px 16px;
    border: 1px solid var(--primary-color, #F93E3E);
    color: var(--primary-color, #F93E3E);
    background: transparent;
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.btn-outline-dynamic:hover {
    background: var(--secondary-color, #FEE9E9);
}

.detail-body {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.detail-row {
    display: flex;
    padding: 0.625rem 0;
    border-bottom: 1px solid #f3f4f6;
    gap: 1rem;
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    flex: 0 0 110px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
}

.detail-value {
    flex: 1;
    font-size: 0.875rem;
    color: #111827;
}

.detail-abilities .detail-value {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.ability-tag {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    color: #eff6ff;
    background: var(--primary-color, #F93E3E);
    border-radius: 4px;
    font-size: 0.75rem;
    font-family: monospace;
}

.modal-content h4 {
    margin-bottom: 1rem;
    color: #111827;
    font-size: 1.25rem;
    font-weight: 600;
}

.modal-content p {
    margin-bottom: 0.5rem;
    color: #6b7280;
    font-size: 0.9375rem;
}

.modal-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
}

@media (max-width: 991px) {
    .filter-action-wrapper {
        flex-direction: column;
        align-items: stretch;
        gap: 20px;
    }

    .filter-controls {
        width: 100%;
    }
    
    .search-box-custom {
        width: 100%;
    }

    .search-type-tabs {
        width: 100%;
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        padding: 6px;
        background: #f8fafc;
        scrollbar-width: none;
    }

    .search-type-tabs::-webkit-scrollbar {
        display: none;
    }

    .tab-btn {
        flex: 0 0 auto;
        white-space: nowrap;
    }

    .action-buttons-group {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
    
    .btn-refresh {
        width: 100%;
    }
}
</style>
